import {
  useCreateAdvance,
  useDeleteAdvance,
  useUpdateAdvance
} from '@services/settlements/mutations/useAdvanceMutations'
import { useImportAdvances } from '@services/settlements/mutations/useImportAdvances'
import type {
  AdvanceKind,
  SettlementDetail
} from '@services/settlements/types'
import { Download, Trash2, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'

import { MaybeCard } from './SectionCard'
import { UnitInput } from './UnitInput'
import { money } from './format'

const KINDS: AdvanceKind[] = ['SERVICES', 'ELECTRICITY', 'GAS']

/**
 * Advances come from the rent records that actually exist, split into their
 * component pots. Nobody ever paid "the rate times 5.5 months".
 */
export function SettlementAdvancesCard({
  settlement,
  readOnly,
  embedded = false
}: {
  settlement: SettlementDetail
  readOnly: boolean
  /** Render without the card chrome, inside a wizard step. */
  embedded?: boolean
}) {
  const { t } = useTranslation('settlement')
  const { confirm, dialog } = useConfirm()

  const importAdvances = useImportAdvances()
  const create = useCreateAdvance()
  const update = useUpdateAdvance()
  const remove = useDeleteAdvance()

  const [kind, setKind] = useState<AdvanceKind>('SERVICES')
  const [amount, setAmount] = useState('')
  const [label, setLabel] = useState('')

  const totals = KINDS.map((current) => ({
    kind: current,
    total: settlement.advances
      .filter((advance) => advance.kind === current)
      .reduce((sum, advance) => sum + Number(advance.amount), 0)
  })).filter((entry) => entry.total !== 0)

  const onAdd = () => {
    if (!amount) return
    create.mutate(
      {
        id: settlement.id,
        body: { kind, amount, period_label: label }
      },
      {
        onSuccess: () => {
          setAmount('')
          setLabel('')
        }
      }
    )
  }

  const onDelete = async (advanceId: string) => {
    if (await confirm({ title: t('advances.delete_confirm'), destructive: true })) {
      remove.mutate({ id: settlement.id, advanceId })
    }
  }

  const action = (
    !readOnly && (
      <Button
        variant="outline"
        size="sm"
        onClick={() => importAdvances.mutate({ id: settlement.id })}
        disabled={importAdvances.isPending}
      >
        <Download className="mr-1.5 h-4 w-4" />
        {settlement.advances.length > 0
          ? t('advances.import_replace')
          : t('advances.import')}
      </Button>
    )
  )

  const body = (
    <>
      {settlement.advances.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          {t('advances.empty')}
        </p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-4">
            {totals.map((entry) => (
              <div key={entry.kind} className="rounded-xl bg-ink/[0.03] px-3 py-2">
                <p className="m-0 text-xs text-muted-foreground">
                  {t(`kinds.${entry.kind}`)}
                </p>
                <p className="m-0 font-semibold tabular-nums text-ink">
                  {money(entry.total)}
                </p>
              </div>
            ))}
          </div>

          <div className="max-h-96 space-y-1.5 overflow-y-auto">
            {settlement.advances.map((advance) => (
              <div
                key={advance.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-ink/[0.08] px-3 py-2 text-sm"
              >
                <span className="w-28 shrink-0 font-medium text-ink">
                  {t(`kinds.${advance.kind}`)}
                </span>
                <span className="w-20 shrink-0 text-muted-foreground">
                  {advance.period_label || '—'}
                </span>
                <span className="w-24 shrink-0 tabular-nums text-ink">
                  {money(advance.amount)}
                </span>
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Switch
                    checked={advance.paid}
                    disabled={readOnly}
                    onCheckedChange={(value) =>
                      update.mutate({
                        id: settlement.id,
                        advanceId: advance.id,
                        body: { paid: value }
                      })
                    }
                  />
                  {t('advances.paid')}
                </span>
                <span className="ml-auto rounded-full bg-ink/[0.05] px-2 py-0.5 text-[11px] text-muted-foreground">
                  {advance.is_manual
                    ? t('advances.manual')
                    : t('advances.from_rent')}
                </span>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(advance.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {!readOnly && (
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="w-44">
            <Combobox
              value={kind}
              searchable={false}
              options={KINDS.map((value) => ({
                value,
                label: t(`kinds.${value}`)
              }))}
              onChange={(value) => setKind((value as AdvanceKind) ?? 'SERVICES')}
            />
          </div>
          <Input
            className="w-28"
            placeholder={t('advances.period')}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
          <UnitInput
            className="w-36"
            unit="€"
            inputMode="decimal"
            placeholder={t('advances.amount')}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <Button onClick={onAdd} disabled={create.isPending}>
            {t('advances.add')}
          </Button>
        </div>
      )}

    </>
  )

  return (
    <>
      <MaybeCard
        embedded={embedded}
        title={t('advances.title')}
        icon={Wallet}
        action={action}
      >
        {body}
      </MaybeCard>
      {dialog}
    </>
  )
}
